import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "../App";
import Sidebar from "../components/Sidebar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { exportTextDocumentToPDF } from '../utils/textDocumentPdf';
import { getTranslation } from '../i18n/translations';

const OCREditor = ({ user, lang = 'tr' }) => {
  const t = (key) => getTranslation(lang, key);
  const navigate = useNavigate();
  const { id: docId } = useParams();
  const editorRef = useRef(null);
  const a4PageRef = useRef(null);
  
  const [documentName, setDocumentName] = useState("");
  const [fontSize, setFontSize] = useState("14");
  const [logo, setLogo] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedDocId, setSavedDocId] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [tempName, setTempName] = useState("");
  const [pdfExporting, setPdfExporting] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const logoInputRef = useRef(null);
  const printFrameRef = useRef(null);

  // Firma Bilgileri
  const [companyName, setCompanyName] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");

  // Müşteri Bilgileri
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  useEffect(() => {
    api.get('/customers').then((res) => setCustomers(res.data || [])).catch(() => {});
  }, []);

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerId(customerId);
    if (customerId === "new") {
      setCustomerName("");
      setCustomerPhone("");
      setCustomerEmail("");
      setCustomerAddress("");
      return;
    }
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setCustomerName(customer.name || "");
      setCustomerPhone(customer.phone || "");
      setCustomerEmail(customer.email || "");
      setCustomerAddress(customer.address || "");
    }
  };

  useEffect(() => {
    if (docId) {
      loadDocument(docId);
    } else {
      // Check for scanned text from TextDocumentsPage (sessionStorage)
      const scannedText = sessionStorage.getItem('ocr_scanned_text');
      const scanTime = sessionStorage.getItem('ocr_scan_time');
      
      // Only use if scanned in last 30 seconds
      if (scannedText && scanTime && (Date.now() - parseInt(scanTime)) < 30000) {
        if (editorRef.current) {
          editorRef.current.innerText = scannedText;
          setHasContent(scannedText.trim().length > 0);
        }
        // Clear after using
        sessionStorage.removeItem('ocr_scanned_text');
        sessionStorage.removeItem('ocr_scan_time');
      } else {
        // Fallback to localStorage (old method)
        const ocrText = localStorage.getItem('ocrExtractedText');
        if (ocrText && editorRef.current) {
          editorRef.current.innerText = ocrText;
          setHasContent(ocrText.trim().length > 0);
        }
      }
    }
    
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        if (response.data) {
          if (response.data.logo) setLogo(response.data.logo);
          if (response.data.companyName) setCompanyName(response.data.companyName);
          if (response.data.phone) setCompanyPhone(response.data.phone);
          if (response.data.email) setCompanyEmail(response.data.email);
          if (response.data.address) setCompanyAddress(response.data.address);
        }
      } catch (e) {}
    };
    fetchSettings();
  }, [docId]);

  const loadDocument = async (id) => {
    try {
      const response = await api.get(`/text-documents/${id}`);
      const doc = response.data;
      if (editorRef.current) {
        editorRef.current.innerHTML = doc.content || '';
        setHasContent((doc.content || '').trim().length > 0);
      }
      setDocumentName(doc.name);
      setFontSize(String(doc.font_size || 14));
      if (doc.logo) setLogo(doc.logo);
      if (doc.company_name) setCompanyName(doc.company_name);
      if (doc.company_phone) setCompanyPhone(doc.company_phone);
      if (doc.company_email) setCompanyEmail(doc.company_email);
      if (doc.company_address) setCompanyAddress(doc.company_address);
      if (doc.customer_name) setCustomerName(doc.customer_name);
      if (doc.customer_phone) setCustomerPhone(doc.customer_phone);
      if (doc.customer_email) setCustomerEmail(doc.customer_email);
      if (doc.customer_address) setCustomerAddress(doc.customer_address);
      setSavedDocId(doc.id);
    } catch (error) {
      toast.error(t('docLoadFailed'));
      navigate('/dashboard');
    }
  };

  // Formatlama komutları
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const setTextColor = (color) => {
    execCommand('foreColor', color);
  };

  const toggleBold = () => execCommand('bold');
  const toggleItalic = () => execCommand('italic');
  const toggleUnderline = () => execCommand('underline');
  const toggleStrikethrough = () => execCommand('strikeThrough');

  // Hizalama
  const alignLeft = () => execCommand('justifyLeft');
  const alignCenter = () => execCommand('justifyCenter');
  const alignRight = () => execCommand('justifyRight');

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Max 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target.result);
        toast.success("Logo eklendi");
      };
      reader.readAsDataURL(file);
    }
  };
  void handleLogoUpload; void logoInputRef;

  const getEditorContent = () => {
    return editorRef.current?.innerHTML || '';
  };

  const getEditorText = () => {
    return editorRef.current?.innerText || '';
  };

  const generateTextOnlyHTML = () => {
    const content = getEditorContent();
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${documentName || 'Belge'}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          body { font-family: Arial, sans-serif; padding: 30px; font-size: ${fontSize}px; line-height: 1.6; }
          .logo-container { text-align: right; margin-bottom: 15px; }
          .logo { max-width: 80px; max-height: 80px; object-fit: contain; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 15px; }
          .company-info, .customer-info { font-size: 12px; }
          .info-title { font-weight: bold; margin-bottom: 5px; color: #1a365d; }
          .content { margin-top: 20px; }
        </style>
      </head>
      <body>
        ${logo ? `<div class="logo-container"><img src="${logo}" class="logo" /></div>` : ''}
        <div class="header">
          <div class="company-info">
            <div class="info-title">${t('pdfCompanyInfo')}</div>
            ${companyName ? `<strong>${companyName}</strong><br/>` : ''}
            ${companyPhone ? `Tel: ${companyPhone}<br/>` : ''}
            ${companyEmail ? `E-posta: ${companyEmail}<br/>` : ''}
            ${companyAddress || ''}
          </div>
          <div class="customer-info" style="text-align:right;">
            <div class="info-title">${t('pdfCustomerInfo')}</div>
            ${customerName ? `<strong>${customerName}</strong><br/>` : ''}
            ${customerPhone ? `Tel: ${customerPhone}<br/>` : ''}
            ${customerEmail ? `E-posta: ${customerEmail}<br/>` : ''}
            ${customerAddress || ''}
          </div>
        </div>
        <div class="content">${content}</div>
      </body>
      </html>
    `;
  };

  const handlePrint = () => {
    if (!getEditorText().trim()) {
      toast.error(t('noContentToPrint'));
      return;
    }

    const html = generateTextOnlyHTML();
    let iframe = printFrameRef.current;
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
      document.body.appendChild(iframe);
      printFrameRef.current = iframe;
    }

    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();

    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        toast.success(t('printWindowOpened'));
      } catch (e) {
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); win.print(); }
        else { toast.error(t('popupBlocked')); }
      }
    }, 500);
  };

  const handlePDF = async () => {
    if (!getEditorText().trim()) {
      toast.error(t('noContentForPdf'));
      return;
    }

    try {
      setPdfExporting(true);
      toast.info(t('pdfGenerating'));

      // Build the same document object the list-page PDF export uses so the output
      // is consistent across Dashboard, TextDocumentsPage and OCREditor (includes
      // logo + Firma Bilgileri + Müşteri Bilgileri + content).
      await exportTextDocumentToPDF({
        name: documentName || t('subjectDocumentPrefix'),
        content: getEditorContent(),
        font_size: parseInt(fontSize, 10) || 14,
        logo: logo || null,
        company_name: companyName,
        company_phone: companyPhone,
        company_email: companyEmail,
        company_address: companyAddress,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        customer_address: customerAddress,
      });

      toast.success(t('pdfDownloaded'));
    } catch (error) {
      console.error('PDF error:', error);
      toast.error(t('pdfCreateFailed') + ": " + (error.message || t('unknownError')));
    } finally {
      setPdfExporting(false);
    }
  };

  const openSaveDialog = () => {
    if (!getEditorText().trim()) {
      toast.error(t('textEmpty'));
      return;
    }
    setTempName(documentName);
    setShowSaveDialog(true);
  };

  const handleSaveAsText = async () => {
    if (!tempName.trim()) {
      toast.error(t('enterAName'));
      return;
    }
    
    setSaving(true);
    setShowSaveDialog(false);
    
    try {
      const docData = {
        name: tempName.trim(),
        content: getEditorContent(),
        logo: logo || null,
        font_size: parseInt(fontSize),
        company_name: companyName,
        company_phone: companyPhone,
        company_email: companyEmail,
        company_address: companyAddress,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        customer_address: customerAddress
      };

      if (savedDocId) {
        await api.put(`/text-documents/${savedDocId}`, docData);
        toast.success(t('docUpdated'));
      } else {
        const response = await api.post('/text-documents', docData);
        setSavedDocId(response.data.id);
        toast.success(t('docSaved'));
      }
      
      setDocumentName(tempName.trim());
      localStorage.removeItem('ocrExtractedText');
    } catch (error) {
      toast.error(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleNewDocument = () => {
    if (editorRef.current) editorRef.current.innerHTML = '';
    setDocumentName("");
    setSavedDocId(null);
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setCustomerAddress("");
    localStorage.removeItem('ocrExtractedText');
    navigate('/ocr-editor');
    toast.info(t('readyForNewDoc'));
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Kaydet Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>📄 {t('saveAsText')}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium text-slate-700 mb-2 block">{t('docNameLabel')}</label>
              <Input
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                placeholder={t('enterDocName')}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSaveAsText()}
              />
              <p className="text-xs text-slate-500 mt-2">{t('textOnlyNote')}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>{t('cancel')}</Button>
              <Button data-testid="dialog-save-btn" onClick={handleSaveAsText} disabled={!tempName.trim()} className="bg-green-600 hover:bg-green-700">
                Kaydet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-base sm:text-lg text-slate-900">{t('textEditorTitle')}</span>
                {documentName && (
                  <span className="text-slate-400 text-xs sm:text-sm hidden sm:inline">• {documentName}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button disabled={saving || !hasContent} size="sm" className="bg-green-500 hover:bg-green-600 text-white text-xs sm:text-sm">
                      {saving ? (
                        <svg className="w-4 h-4 sm:mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                      )}
                      <span className="hidden sm:inline">{t('save')}</span>
                      <svg className="w-4 h-4 ml-1 sm:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={openSaveDialog} className="cursor-pointer py-3">
                      <svg className="w-5 h-5 mr-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <div className="font-medium">{t('saveAsText')}</div>
                        <div className="text-xs text-slate-500">{t('saveToDb')}</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handlePDF} className="cursor-pointer py-3">
                      <svg className="w-5 h-5 mr-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <div className="font-medium">{t('downloadAsPdf')}</div>
                        <div className="text-xs text-slate-500">{t('pdfFormat')}</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handlePrint} className="cursor-pointer py-3">
                      <svg className="w-5 h-5 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      <div>
                        <div className="font-medium">{t('print')}</div>
                        <div className="text-xs text-slate-500">{t('sendToPrinter')}</div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button variant="outline" size="sm" onClick={() => navigate('/text-documents')} className="text-slate-600 hover:bg-slate-100 text-xs sm:text-sm">
                  <span className="hidden sm:inline">✕ </span>{t('close')}
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Ana İçerik */}
        <main className="p-4 sm:p-6">
          <div className="max-w-5xl mx-auto space-y-4">
          
          {/* Genel Bilgiler — Yeni Teklif sayfasıyla aynı görünüm */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 px-1">Genel Bilgiler</h2>

            <div className="grid grid-cols-1 gap-4">
              {/* Belge Bilgileri */}
              <Card className="bg-white border-2 border-teal-200 shadow-sm">
                <CardHeader className="px-4 py-3 border-b border-slate-100">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Belge Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-4 py-4">
                  <div>
                    <Label className="text-sm">{t('docNameLabel')} <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="doc-name-input"
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                      placeholder={t('docNamePlaceholder')}
                      className="rounded-lg border-slate-300 focus:border-teal-500 bg-white"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Müşteri ve İletişim Bilgileri */}
              <Card className="bg-white border-2 border-slate-200 shadow-sm">
                <CardHeader className="px-4 py-3 border-b border-slate-100">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t('customerContactInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-sm">{t('selectCustomerLabel')}</Label>
                      <Select value={selectedCustomerId} onValueChange={handleCustomerSelect}>
                        <SelectTrigger className="rounded-lg border-slate-300 focus:border-blue-500 bg-white" data-testid="ocr-customer-select">
                          <SelectValue placeholder={t('selectCustomerPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">
                            <span className="flex items-center gap-2 text-amber-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              {t('addNewCustomer')}
                            </span>
                          </SelectItem>
                          {customers.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>
                              <span className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-medium">
                                  {customer.name?.charAt(0)?.toUpperCase() || 'M'}
                                </span>
                                <span>{customer.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">{t('customerName')}</Label>
                      <Input data-testid="ocr-customer-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder={t('customerNamePlaceholder')} className="rounded-lg border-slate-300 focus:border-blue-500 bg-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-sm">Telefon</Label>
                      <Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+90 555 123 45 67" className="rounded-lg border-slate-300 focus:border-blue-500 bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm">E-posta</Label>
                      <Input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="musteri@example.com" className="rounded-lg border-slate-300 focus:border-blue-500 bg-white" />
                    </div>
                    <div>
                      <Label className="text-sm">Adres</Label>
                      <Input value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder={t('customerAddressPlaceholder')} className="rounded-lg border-slate-300 focus:border-blue-500 bg-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Metin Düzenleyici Kartı - A4 Word Benzeri */}
          <div className="bg-slate-200 rounded-lg p-4 sm:p-6">
            {/* Word Benzeri Toolbar */}
            <div className="bg-white border border-slate-300 rounded-t-lg py-2 px-4 flex flex-wrap items-center gap-2 max-w-[210mm] mx-auto shadow-sm">
              {/* Yazı Boyutu */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500">{t('fontLabel')}</span>
                <Select value={fontSize} onValueChange={setFontSize}>
                  <SelectTrigger className="w-14 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36].map(size => (
                      <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ayraç */}
              <div className="w-px h-6 bg-slate-300"></div>
              
              {/* Formatlama Butonları */}
              <div className="flex items-center">
                <button onClick={toggleBold} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 font-bold text-slate-700" title={t('boldTitle')}>B</button>
                <button onClick={toggleItalic} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 italic text-slate-700" title={t('italicTitle')}>I</button>
                <button onClick={toggleUnderline} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 underline text-slate-700" title={t('underlineTitle')}>U</button>
                <button onClick={toggleStrikethrough} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 line-through text-slate-700" title={t('strikeTitle')}>S</button>
              </div>

              {/* Ayraç */}
              <div className="w-px h-6 bg-slate-300"></div>

              {/* Renk Seçiciler */}
              <div className="flex items-center gap-1">
                <button onClick={() => setTextColor('#000000')} className="w-5 h-5 rounded bg-black border border-slate-400" title="Siyah" />
                <button onClick={() => setTextColor('#DC2626')} className="w-5 h-5 rounded bg-red-600 border border-slate-400" title="" />
                <button onClick={() => setTextColor('#16A34A')} className="w-5 h-5 rounded bg-green-600 border border-slate-400" title="" />
                <button onClick={() => setTextColor('#2563EB')} className="w-5 h-5 rounded bg-blue-600 border border-slate-400" title="Mavi" />
              </div>

              {/* Ayraç */}
              <div className="w-px h-6 bg-slate-300"></div>

              {/* Hizalama Butonları */}
              <div className="flex items-center">
                <button onClick={alignLeft} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100" title="Sola">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h14" /></svg>
                </button>
                <button onClick={alignCenter} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100" title="Ortala">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M5 18h14" /></svg>
                </button>
                <button onClick={alignRight} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100" title={t('alignRightTitle')}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M6 18h14" /></svg>
                </button>
              </div>

              {/* PDF Export butonu */}
              {savedDocId && (
                <Button variant="outline" size="sm" onClick={handleNewDocument} className="ml-auto text-xs h-7">+ Yeni</Button>
              )}
            </div>

            {/* Kayıtlı belge bilgisi */}
            {savedDocId && documentName && (
              <div className="bg-green-50 border-x border-green-200 py-1 px-4 max-w-[210mm] mx-auto">
                <span className="text-green-700 text-xs">✓ "{documentName}" kaydedildi</span>
              </div>
            )}

            {/* A4 Kağıt Görünümü */}
            <div 
              ref={a4PageRef}
              className="mx-auto bg-white relative"
              style={{
                width: '210mm',
                minHeight: '297mm',
                maxWidth: '100%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.1)'
              }}
            >
              {/* Rich Text Editor - A4 Margins */}
              <div
                ref={editorRef}
                contentEditable
                className="w-full outline-none relative z-10"
                style={{ 
                  fontFamily: 'Arial, sans-serif',
                  fontSize: `${fontSize}px`,
                  lineHeight: '1.6',
                  padding: '25mm 20mm 20mm 25mm',
                  minHeight: '297mm',
                  boxSizing: 'border-box'
                }}
                data-placeholder={t('writeTextHere')}
                onFocus={(e) => {
                  if (e.target.innerText === '') {
                    e.target.classList.add('empty');
                  }
                }}
                onInput={(e) => {
                  const text = e.target.innerText.trim();
                  setHasContent(text.length > 0);
                  if (text === '') {
                    e.target.classList.add('empty');
                  } else {
                    e.target.classList.remove('empty');
                  }
                }}
              />
            </div>
            
            {/* Sayfa Bilgisi */}
            <div className="text-center text-xs text-white/80 mt-4 flex items-center justify-center gap-4">
              <span>📐 A4: 210mm × 297mm</span>
              <span>|</span>
              <span>⬅️ Sol: 25mm</span>
              <span>➡️ Sağ: 20mm</span>
              <span>⬆️ Üst: 25mm</span>
              <span>⬇️ Alt: 20mm</span>
            </div>

            <style>{`
              [contenteditable]:empty:before {
                content: attr(data-placeholder);
                color: #9ca3af;
                pointer-events: none;
              }
              @media (max-width: 900px) {
                .mx-auto[style*="220mm"] {
                  width: 100% !important;
                  min-height: 500px !important;
                }
              }
            `}</style>
          </div>

        </div>
        </main>
      </div>
    </div>
  );
};

export default OCREditor;
